import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
    Box,
    ClickAwayListener,
    Typography,
    Avatar,
    IconButton,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { alpha } from "@mui/material/styles";
import EloBadge from "../common/EloBadge.jsx";
import Tooltip from "../common/Tooltip";
import utils from "../../universal/utils";
import { pickAvatarUpdatedAtMs, resolveAvatarSrc } from "../../utils/avatarUrl";
import { getPendingRequestRecipientSteamId } from "../../utils/friendRequests";
import { toProfilePath } from "../../universal/v2Routes";
import FriendRowPreviewCard from "./FriendRowPreviewCard";
import { claimFriendRowMenu, subscribeFriendRowMenu } from "./friendRowMenu";

const basePath = utils.getEdgeBasePath();

const MOTION_OUT_MS = 280;
const ROW_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const HOVER_TRANSITION = `all 180ms ${ROW_EASE}`;

const requestRowKeyframes = {
    "@keyframes requestRowMount": {
        from: {
            opacity: 0,
            transform: "translateY(8px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },
};

export default function RequestRow({
    request,
    handleAcceptFriendRequest,
    handleDeclineFriendRequest,
    friendRequestToCancel,
    listIndex = 0,
}) {
    const [acceptSlideAnimation, setAcceptSlideAnimation] = useState(false);
    const [declineSlideAnimation, setDeclineSlideAnimation] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const rowRef = useRef(null);
    const recipientSteamId = getPendingRequestRecipientSteamId(request);
    const requestKey = String(request?._id || request?.fromUser?._id || "");

    useEffect(() => {
        return subscribeFriendRowMenu((openId) => {
            if (openId !== requestKey) setMenuOpen(false);
        });
    }, [requestKey]);

    const staggerMs = Math.min(listIndex * 48, 360);

    const avatarSrc = resolveAvatarSrc({
        basePath,
        picture: request.fromUser?.picture,
        userId: request.fromUser?.userId || request.fromUser?._id,
        avatarUpdatedAtMs: pickAvatarUpdatedAtMs(request.fromUser),
        hasCustomAvatar: !!request.fromUser?.avatar,
    });

    const handleAccept = () => {
        if (!recipientSteamId) {
            return;
        }
        Promise.resolve(handleAcceptFriendRequest(request.fromUser.steamId, recipientSteamId))
            .then((result) => {
                if (!result?.ok) {
                    return;
                }
                setAcceptSlideAnimation(true);
                setTimeout(() => {
                    friendRequestToCancel(request._id);
                    setAcceptSlideAnimation(false);
                }, MOTION_OUT_MS + 40);
            })
            .catch(() => {});
    };

    const handleDecline = () => {
        if (!recipientSteamId) {
            return;
        }
        Promise.resolve(handleDeclineFriendRequest(request.fromUser.steamId, recipientSteamId))
            .then((result) => {
                if (!result?.ok) {
                    return;
                }
                setDeclineSlideAnimation(true);
                setTimeout(() => {
                    friendRequestToCancel(request._id);
                    setDeclineSlideAnimation(false);
                }, MOTION_OUT_MS + 40);
            })
            .catch(() => {});
    };

    const handleProfile = () => {
        const profilePath = toProfilePath(request?.fromUser?._id, location.pathname);
        if (profilePath) {
            setMenuOpen(false);
            history.push(profilePath);
        }
    };

    const handleToggleMenu = () => {
        setMenuOpen((prev) => {
            const next = !prev;
            if (next) claimFriendRowMenu(requestKey);
            return next;
        });
    };

    const rowCardSx = {
        ...requestRowKeyframes,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        minHeight: "3rem",
        gap: { xs: "0.42rem", xl: "0.48rem" },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        padding: { xs: "0.32rem 0.52rem", xl: "0.35rem 0.58rem" },
        marginBottom: { xs: "0.34rem", xl: "0.38rem" },
        background:
            "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.015) 100%)",
        border: `1px solid ${colors.border.default}`,
        borderRadius: "8px",
        boxShadow:
            "0 8px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03)",
        transition: HOVER_TRANSITION,
        position: "relative",
        cursor: "pointer",
        overflow: "hidden",
        "&:hover": {
            background:
                "linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.022) 100%)",
            border: `1px solid ${alpha(colors.primary.main, 0.5)}`,
            boxShadow:
                "0 10px 28px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.04), 0 0 18px rgba(255,190,70,0.08)",
            transform: "translateY(-2px)",
        },
        ...(acceptSlideAnimation && {
            transform: "translateX(-10px) scale(0.97)",
            opacity: 0,
            transition: `opacity ${MOTION_OUT_MS}ms ${ROW_EASE}, transform ${MOTION_OUT_MS}ms ${ROW_EASE}`,
            pointerEvents: "none",
            "&:hover": {
                transform: "translateX(-10px) scale(0.97)",
                border: `1px solid ${colors.border.default}`,
                boxShadow:
                    "0 8px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03)",
            },
        }),
        ...(declineSlideAnimation && {
            transform: "translateX(10px) scale(0.97)",
            opacity: 0,
            transition: `opacity ${MOTION_OUT_MS}ms ${ROW_EASE}, transform ${MOTION_OUT_MS}ms ${ROW_EASE}`,
            pointerEvents: "none",
            "&:hover": {
                transform: "translateX(10px) scale(0.97)",
                border: `1px solid ${colors.border.default}`,
                boxShadow:
                    "0 8px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03)",
            },
        }),
    };

    const acceptBtnSx = {
        width: 30,
        height: 30,
        minWidth: 30,
        padding: 0,
        borderRadius: "8px",
        color: "rgba(255,255,255,0.95)",
        background: alpha(colors.state.success.main, 0.4),
        border: `1px solid ${alpha(colors.state.success.main, 0.62)}`,
        boxShadow: `0 0 12px ${alpha(colors.state.success.main, 0.22)}`,
        transition: `background-color 180ms ${ROW_EASE}, border-color 180ms ${ROW_EASE}, box-shadow 180ms ease, transform 180ms ${ROW_EASE}`,
        "&:hover": {
            background: alpha(colors.state.success.hover, 0.5),
            borderColor: alpha(colors.state.success.main, 0.78),
            boxShadow: `0 0 18px ${alpha(colors.state.success.main, 0.38)}`,
            transform: "translateY(-1px) scale(1.02)",
        },
        "&:active": {
            transform: "scale(0.98)",
        },
    };

    const declineBtnSx = {
        ...acceptBtnSx,
        background: alpha(colors.state.danger.main, 0.38),
        border: `1px solid ${alpha(colors.state.danger.main, 0.58)}`,
        boxShadow: `0 0 10px ${alpha(colors.state.danger.main, 0.2)}`,
        "&:hover": {
            background: alpha(colors.state.danger.hover, 0.48),
            borderColor: alpha(colors.state.danger.main, 0.74),
            boxShadow: `0 0 16px ${alpha(colors.state.danger.main, 0.34)}`,
            transform: "translateY(-1px) scale(1.02)",
        },
        "&:active": {
            transform: "scale(0.98)",
        },
    };

    return (
        <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
        <Box
            sx={{
                ...requestRowKeyframes,
                width: "100%",
                opacity: 0,
                animation: `requestRowMount 220ms ${ROW_EASE} ${staggerMs}ms forwards`,
            }}
        >
            <Box
                ref={rowRef}
                className="request-row-root"
                role="button"
                tabIndex={0}
                aria-expanded={menuOpen}
                aria-haspopup="dialog"
                onClick={handleToggleMenu}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleToggleMenu();
                    }
                }}
                sx={rowCardSx}
            >
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                    <Box
                        sx={{
                            position: "relative",
                            width: { xs: "2.4rem", xl: "2.7rem" },
                            height: { xs: "2.4rem", xl: "2.7rem" },
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: `
                                radial-gradient(
                                    circle at top,
                                    rgba(246,185,76,0.16),
                                    rgba(255,255,255,0.03)
                                )
                            `,
                            transition: "all 260ms ease",
                            border: "1px solid rgba(255,255,255,0.08)",
                            boxShadow: `
                                inset 0 1px 0 rgba(255,255,255,0.06),
                                0 10px 24px rgba(0,0,0,0.35),
                                0 0 30px rgba(246,185,76,0.08)
                            `,
                            ".request-row-root:hover &": {
                                transform: "scale(1.03)",
                            },
                        }}
                    >
                        <Avatar
                            src={avatarSrc}
                            alt={request.fromUser?.nickname || "Unknown User"}
                            sx={{
                                position: "relative",
                                width: "90%",
                                height: "90%",
                                border: "2px solid rgba(255,255,255,0.08)",
                                boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: "0.12rem",
                        py: "0.04rem",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: { xs: "0.28rem", xl: "0.32rem" },
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                color: "#ffffff",
                                fontSize: { xs: "0.76rem", xl: "0.82rem" },
                                fontWeight: 700,
                                letterSpacing: "0.02em",
                                lineHeight: 1.15,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flexShrink: 1,
                                minWidth: 0,
                                transition: "filter 180ms ease, color 180ms ease",
                                ".request-row-root:hover &": {
                                    filter: "brightness(1.06)",
                                },
                            }}
                        >
                            {request.fromUser?.nickname || "Unknown User"}
                        </Typography>
                        <Box
                            sx={{
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                opacity: 0.92,
                                transition: `opacity 180ms ${ROW_EASE}`,
                                ".request-row-root:hover &": {
                                    opacity: 1,
                                },
                            }}
                        >
                            <EloBadge value={request.fromUser?.elo} size="small" />
                        </Box>
                    </Box>
                    <Typography
                        sx={{
                            color: "rgba(255,255,255,0.52)",
                            fontSize: { xs: "0.62rem", xl: "0.66rem" },
                            fontWeight: 400,
                            lineHeight: 1.25,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            minWidth: 0,
                        }}
                    >
                        {request.message || "Wants to add you as a friend."}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: "0.28rem", xl: "0.32rem" },
                        flexShrink: 0,
                    }}
                >
                    <Tooltip title="Accept">
                        <IconButton
                            onClick={(event) => {
                                event.stopPropagation();
                                handleAccept();
                            }}
                            size="small"
                            aria-label="Accept friend request"
                            disableRipple
                            sx={acceptBtnSx}
                        >
                            <Check sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Decline">
                        <IconButton
                            onClick={(event) => {
                                event.stopPropagation();
                                handleDecline();
                            }}
                            size="small"
                            aria-label="Decline friend request"
                            disableRipple
                            sx={declineBtnSx}
                        >
                            <Close sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <FriendRowPreviewCard
                open={menuOpen}
                anchorEl={rowRef.current}
                friendData={request.fromUser}
                avatarSrc={avatarSrc}
                isOnline={request.fromUser?.isOnline === true}
                playerStatus={request.fromUser?.playerStatus}
                onProfile={handleProfile}
                onClose={() => setMenuOpen(false)}
            />
        </Box>
        </ClickAwayListener>
    );
}
